import exportFromJSON from "export-from-json";
import {
	IReport_DisciplineCode,
	IReport_DisciplineProject,
	IReport_IncompleteMedia,
	ReportFileFormat,
	ReportType,
} from "../../models/IReport";

/**
 * Gera um arquivo com o tipo e nome indicados, e então, realiza o download do arquivo
 * @param format O formato de arquivo a ser baixado
 * @param data Os dados para gerar o arquivo
 * @param name O nome do arquivo a ser baixado
 */
export function generateFile(
	format: ReportFileFormat,
	data: any,
	name: string
) {
	const fileName = name ?? "Relatório";
	let exportType;

	switch (format) {
		case "csv":
			exportType = exportFromJSON.types.csv;
			break;
		case "xml":
			exportType = exportFromJSON.types.xml;
			break;
		case "xlsx":
			exportType = exportFromJSON.types.xls;
			break;
	}

	exportFromJSON({ data, fileName, exportType });
}

/**
 * Realiza os tratamentos necessários na resposta da API para emissão de um relatório do tipo indicado
 * @param data os dados a serem tratados
 * @param report_type o tipo de relatório que remete a esses dados
 * @returns dados prontos para exportação
 */
export function prepareDataForReport(data: any[], report_type: ReportType) {
	switch (report_type) {
		case "discipline_project":
			return prepareForDisciplineProject(data);
		case "discipline_code":
			return prepareForDisciplineCode(data);
		case "incomplete_media":
			return prepareForIncompleteMedia(data);
		case "user_discipline":
			return prepareForUserDisciplineReport(data);
		case "medias_report":
			return prepareForMediasReport(data);
		default:
			throw new Error("Tipo de relatório não implementado");
	}
}

/** Trato a resposta da API para emissão de relatório
 * Nesse caso, cada unidade precisa ser um item do array (linha no excel), e os objetos são cada um uma coluna
 */
function prepareForDisciplineProject(data: IReport_DisciplineProject[]) {
	let lines: any[] = [];

	data.forEach((project) => {
		if (project.disciplines.length > 0) {
			project.disciplines.forEach((discipline) => {
				// preciso trazer ao menos essas infos
				let builtDiscipline: any = {
					Projeto: project.project ?? "",
					Disciplina: discipline.title ?? "",
					Parceiro: discipline.responsible ?? "",
					Código: discipline.code ?? "",
					"Publicado Locacao":
						prepareBooleanData(discipline.is_published) ?? "",
					"Publicado Loja":
						prepareBooleanData(discipline.is_published_store) ?? "",
				};

				// se tenho unidades, adiciono cada uma como uma nova linha
				if (discipline.items.length > 0) {
					let disciplineCopy = { ...builtDiscipline };

					discipline.items.forEach((unit) => {
						disciplineCopy["Unidade"] = unit.title ?? "";

						if (unit.medias.length > 0) {
							unit.medias.forEach((media, index) => {
								// insiro cada objeto como um novo atributo, nomeado no seguinte formato: "Objeto 1"
								disciplineCopy[`Objeto ${index + 1}`] =
									media.title ?? "";
							});
						}
					});

					lines.push(disciplineCopy);
				} else {
					// se não só o básico
					lines.push(builtDiscipline);
				}
			});
		}
	});

	return lines;
}

/** Trato a resposta da API para emissão de relatório
 * Nesse caso, renomeio e reordeno os campos
 */
function prepareForDisciplineCode(data: IReport_DisciplineCode[]) {
	return data.map((dataItem) => {
		return {
			Disciplina: dataItem.title ?? "",
			Código: dataItem.code ?? "",
			Parceiro: dataItem.responsible__name ?? "",
			"Publicado locacao":
				prepareBooleanData(dataItem.is_published) ?? "",
			"Publicado loja":
				prepareBooleanData(dataItem.is_published_store) ?? "",
			"Carga horária": dataItem.workload ?? "",
			Ementa: prepareHtmlData(dataItem.description) ?? "",
			"Área do conhecimento": dataItem.knowledge_area__list,
			"Nível de formação": dataItem.modalities,
			Prazo: dataItem.get_deadline_display,
		};
	});
}

/** Trato a resposta da API para emissão de relatório
 * Nesse caso, renomeio e reordeno os campos
 */
function prepareForIncompleteMedia(data: IReport_IncompleteMedia[]) {
	return data.map((dataItem) => {
		return {
			Objeto: dataItem.title ?? "",
			Disciplina: dataItem.discipline ?? "",
			Código: dataItem.code ?? "",
			Unidade: dataItem.item__title ?? "",
			"Publicado locacao":
				prepareBooleanData(dataItem.is_published) ?? "",
			"Publicado loja":
				prepareBooleanData(dataItem.is_published_store) ?? "",
		};
	});
}

/** Trato a resposta da API para emissão de relatório
 * Nesse caso, renomeio e reordeno os campos
 */
function prepareForMediasReport(data: IReport_IncompleteMedia[]) {
	return data.map((dataItem) => {
		return {
			Objeto: dataItem.title ?? "",
			Disciplina: dataItem.discipline ?? "",
			"Código da disciplina": dataItem.discipline_code ?? "",
			Unidade: dataItem.item__title ?? "",
			"Publicado locacao":
				prepareBooleanData(dataItem.is_published) ?? "",
			"Publicado loja":
				prepareBooleanData(dataItem.is_published_store) ?? "",
		};
	});
}

/** Trato a resposta da API para emissão de relatório
 * Nesse caso, renomeio e reordeno os campos
 */
function prepareForUserDisciplineReport(data: any[]) {
	return data.map((dataItem) => {
		return {
			"Nome do cliente": dataItem.client_name ?? "",
			"Situação do plano":
				prepareSignatureData(dataItem.signature_status) ?? "",
			Disciplina: dataItem.title ?? "",
			Parceiro: dataItem.discipline_responsible ?? "",
			"Código da disciplina": dataItem.discipline_code ?? "",
			Ativo: dataItem.discipline_status ?? "",
			Plano: dataItem.signature_plan_name ?? "",
			"Vencimento do plano": dataItem.signature_end ?? "",
			"E-mail": dataItem.client_email ?? "",
		};
	});
}

/**
 * Realiza a tratativo do booleando
 */
function prepareBooleanData(data: boolean) {
	if (data === true) {
		return "Sim";
	} else if (data === false) {
		return "Não";
	}
	return "";
}

function prepareHtmlData(data: string) {
	if (data) {
		const regex = /(<([^>]+)>)/gi;
		const result = data.replace(regex, "");
		return result;
	}
	return data;
}

/**
 * Realiza a tratativo do plano
 */
function prepareSignatureData(data: string) {
	if (data === "ACTIVE") {
		return "Ativo";
	} else if (data === "INACTIVE") {
		return "Inativo";
	} else {
		return "Cancelado";
	}
}

function prepareHtmlString(data: string) {
	console.log(data);
	const regex = /(<([^>]+)>)/gi;
	const result = data.replace(regex, "").replace(/&nbsp;/g, " ");
	return result;
}
